import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { ItineraryMap } from "./ItineraryMap";
import { TripAssistant } from "./TripAssistant";
import { BudgetPlanner } from "./BudgetPlanner";
import { PlaceBadges, placePhotoUrl } from "./PlaceBadges";
import { ClosureGuard } from "./ClosureGuard";
import { MapPin, Utensils, Gem, CalendarDays, Wallet, Clock, Quote, RefreshCw, Printer, Loader2, CloudSun, Thermometer, Umbrella, Luggage, AlertTriangle, BedDouble, Leaf, ExternalLink, Bus, Star, Accessibility, Baby, PiggyBank, Plane, TrainFront, Car, ArrowRight, Route, Pencil, Check, StickyNote, ListChecks, CheckSquare, Square, Lightbulb, PartyPopper, CalendarClock, CalendarPlus } from "lucide-react";

const transferIcon = (mode = "") => {
  const m = mode.toLowerCase();
  if (m.includes("flight") || m.includes("fly") || m.includes("plane") || m.includes("air")) return Plane;
  if (m.includes("train") || m.includes("rail")) return TrainFront;
  if (m.includes("car") || m.includes("drive") || m.includes("taxi") || m.includes("cab")) return Car;
  return Bus;
};

const DEST_IMG =
  "https://images.unsplash.com/photo-1610191434707-fc7b16541c1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxuYXR1cmUlMjBsYW5kc2NhcGUlMjB0cmF2ZWx8ZW58MHx8fHwxNzg2MzI1MTU0fDA&ixlib=rb-4.1.0&q=85";
const GEM_IMG =
  "https://images.unsplash.com/photo-1762275167395-e50462b6595c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxoaWRkZW4lMjBnZW0lMjBsb2NhbCUyMGNhZmV8ZW58MHx8fHwxNzg2MzI1MTU0fDA&ixlib=rb-4.1.0&q=85";

const stagger = {
  hidden: { opacity: 0, y: 16 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.05 } }),
};

export const TripDisplay = ({ trip, editable = false, onTripChange }) => {
  const [busyDay, setBusyDay] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [whyLoading, setWhyLoading] = useState(false);
  const [localWhy, setLocalWhy] = useState(null);
  if (!trip) return null;
  const places = trip.places || [];
  const restaurants = trip.restaurants || [];
  const gems = trip.hidden_gems || [];
  const itinerary = trip.itinerary || [];
  const accommodations = trip.accommodations || [];
  const transfers = trip.transfers || [];
  const packing = trip.packing_list || [];
  const checkedSet = new Set(trip.packing_checked || []);
  const req = trip._request || {};
  const displayName = trip.custom_name || trip.destination;
  const why = trip.why_explanations || localWhy;

  const fetchWhy = async () => {
    setWhyLoading(true);
    try {
      const { data } = await api.post("/trips/explain", { trip });
      if (onTripChange) onTripChange({ ...trip, why_explanations: data }, { label: "Explanations" });
      else setLocalWhy(data);
    } catch (err) {
      toast.error("Couldn't generate explanations right now.");
    } finally {
      setWhyLoading(false);
    }
  };

  const saveName = () => {
    onTripChange?.({ ...trip, custom_name: nameDraft.trim() });
    setEditingName(false);
  };
  const updateDayNote = (day, note) => {
    onTripChange?.({ ...trip, itinerary: itinerary.map((x) => (x.day === day ? { ...x, note } : x)) });
  };
  const togglePack = (item) => {
    const next = new Set(checkedSet);
    next.has(item) ? next.delete(item) : next.add(item);
    onTripChange?.({ ...trip, packing_checked: [...next] });
  };

  const printPacking = () => {
    const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const rows = packing
      .map((it) => `<li><span class="box">${checkedSet.has(it) ? "&#9745;" : "&#9744;"}</span>${esc(it)}</li>`)
      .join("");
    const tip = trip.weather?.packing_tip ? `<p class="tip">Tip: ${esc(trip.weather.packing_tip)}</p>` : "";
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Packing list — ${esc(displayName)}</title>
<style>
  @page { margin: 22mm; }
  body { font-family: Georgia, 'Times New Roman', serif; color: #14110d; max-width: 620px; margin: 40px auto; padding: 0 24px; }
  .brand { font-size: 13px; letter-spacing: 2px; text-transform: uppercase; color: #C8553D; margin: 0 0 4px; }
  h1 { font-size: 28px; margin: 0 0 2px; }
  .sub { color: #6b6459; font-size: 14px; margin: 0 0 22px; }
  .tip { background: #F3EFE8; border-left: 3px solid #1A3B2B; padding: 10px 14px; font-size: 13px; color: #3a352d; margin: 0 0 20px; }
  ul { list-style: none; padding: 0; margin: 0; column-count: 2; column-gap: 34px; }
  li { break-inside: avoid; font-size: 15px; padding: 7px 0; border-bottom: 1px solid #eee4d4; }
  .box { color: #C8553D; font-size: 18px; margin-right: 10px; vertical-align: -1px; }
  .ft { margin-top: 26px; color: #9a9385; font-size: 12px; }
</style></head><body onload="window.print()">
  <p class="brand">WanderlyMe</p>
  <h1>Packing list</h1>
  <p class="sub">${esc(displayName)}${trip.days ? ` · ${trip.days} days` : ""}</p>
  ${tip}
  <ul>${rows}</ul>
  <p class="ft">Generated with WanderlyMe · Travel planned around you</p>
</body></html>`;
    const w = window.open("", "_blank", "width=720,height=900");
    if (!w) { toast.error("Allow pop-ups to print your packing list."); return; }
    w.document.write(html);
    w.document.close();
  };

  const downloadCalendar = () => {
    const startStr = trip._request?.travel_start_date || "";
    if (!startStr) {
      toast.info("Set an exact start date on the trip to export dated calendar events.");
      return;
    }
    const start = new Date(startStr + "T00:00:00");
    if (isNaN(start)) { toast.error("Couldn't read the trip start date."); return; }
    const pad = (n) => String(n).padStart(2, "0");
    const ymd = (d) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
    const esc = (s) => String(s || "").replace(/([,;\\])/g, "\\$1").replace(/\n/g, "\\n");
    const dest = trip.custom_name || trip.destination || "Trip";
    const stamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//WanderlyMe//Trip//EN", "CALSCALE:GREGORIAN"];
    const addEvent = (dayOffset, summary, desc) => {
      const d0 = new Date(start); d0.setDate(start.getDate() + dayOffset);
      const d1 = new Date(d0); d1.setDate(d0.getDate() + 1);
      lines.push(
        "BEGIN:VEVENT",
        `UID:${Math.random().toString(36).slice(2)}-${ymd(d0)}@wanderlyme`,
        `DTSTAMP:${stamp}`,
        `DTSTART;VALUE=DATE:${ymd(d0)}`,
        `DTEND;VALUE=DATE:${ymd(d1)}`,
        `SUMMARY:${esc(summary)}`,
        `DESCRIPTION:${esc(desc)}`,
        "END:VEVENT",
      );
    };
    itinerary.forEach((d, i) => {
      const acts = ((d.activities && d.activities.length ? d.activities : d.stops) || [])
        .map((a) => a.name).filter(Boolean).join(", ");
      const desc = acts || [d.morning, d.afternoon, d.evening].filter(Boolean).join(" · ");
      addEvent(i, `${dest} · Day ${d.day}: ${d.title || ""}`.trim(), desc);
    });
    (trip.events || []).forEach((ev) => {
      if (ev && ev.day != null && ev.day !== "") {
        addEvent(Number(ev.day) - 1, `${ev.name}${ev.type ? ` (${ev.type})` : ""}`, ev.description || "");
      }
    });
    lines.push("END:VCALENDAR");
    const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${dest.replace(/[^\w]+/g, "-").toLowerCase()}-wanderlyme.ics`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("Calendar file downloaded — open it to add the trip to Google or Apple Calendar.");
  };

  const bookingLink = (item, cityFallback) => {
    const link = (item.link || "").trim();
    if (/^https?:\/\//i.test(link)) return link;
    const q = encodeURIComponent(`${item.name} ${item.area || item.city || cityFallback || trip.destination || ""}`);
    return `https://www.google.com/maps/search/?api=1&query=${q}`;
  };

  const cb = trip.cost_breakdown;
  const fmtMoney = (n) => {
    const num = Number(n);
    if (!isFinite(num)) return null;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: (cb?.currency || "USD").toUpperCase(),
        maximumFractionDigits: 0,
      }).format(num);
    } catch {
      return `${cb?.currency || ""} ${Math.round(num)}`;
    }
  };
  const costRows = cb
    ? [
        { key: "accommodation", label: "Accommodation", icon: BedDouble },
        { key: "food", label: "Food & drink", icon: Utensils },
        { key: "activities", label: "Activities", icon: MapPin },
        { key: "transport", label: "Transport", icon: Bus },
      ].filter((r) => Number(cb[r.key]) > 0)
    : [];
  const costMax = cb ? Math.max(...costRows.map((r) => Number(cb[r.key]) || 0), 1) : 1;
  const targetNum = (() => {
    const raw = (req.target_budget || "").toString().replace(/,/g, "");
    const m = raw.match(/\d+(\.\d+)?/);
    return m ? Number(m[0]) : null;
  })();
  const overBudget = cb && targetNum != null && Number(cb.total) > targetNum;

  // Group accommodations by city (multi-city support)
  const accomByCity = accommodations.reduce((acc, a) => {
    const key = a.city || trip.destination || "Your stay";
    (acc[key] = acc[key] || []).push(a);
    return acc;
  }, {});

  const regenerateDay = async (d) => {
    setBusyDay(d.day);
    try {
      const { data } = await api.post("/trips/regenerate-day", {
        destination: trip.destination || req.destination || "",
        country: trip.country || "",
        budget: req.budget || "Mid-range",
        travelers: req.travelers || "2 adults",
        interests: req.interests || "",
        accessibility: req.accessibility || "",
        city: d.city || "",
        accommodation_area: accommodations[0]
          ? `${accommodations[0].area || ""}${accommodations[0].city ? ", " + accommodations[0].city : ""}`.trim()
          : "",
        day: d.day,
        total_days: itinerary.length,
        avoid: `${d.title}. Morning: ${d.morning}. Afternoon: ${d.afternoon}. Evening: ${d.evening}`,
      });
      const newItinerary = itinerary.map((x) => (x.day === d.day ? data.day : x));
      onTripChange?.({ ...trip, itinerary: newItinerary });
      toast.success(`Day ${d.day} reshuffled!`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reshuffle day");
    } finally {
      setBusyDay(null);
    }
  };

  return (
    <div className="space-y-16 print-area" data-testid="trip-display">
      {/* Header */}
      <div className="relative overflow-hidden rounded-3xl border border-black/5">
        <img src={trip.cover_image || DEST_IMG} alt={trip.destination} className="w-full h-72 sm:h-80 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="no-print absolute top-5 right-5 flex items-center gap-2">
          <button
            data-testid="trip-calendar-btn"
            onClick={downloadCalendar}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 text-[#1A3B2B] text-sm hover:bg-white transition-colors"
          >
            <CalendarPlus className="w-4 h-4" /> Add to calendar
          </button>
          <button
            data-testid="trip-print-btn"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 text-[#1A3B2B] text-sm hover:bg-white transition-colors"
          >
            <Printer className="w-4 h-4" /> Save as PDF
          </button>
        </div>
        <div className="absolute bottom-0 p-8 sm:p-10 text-[#FDFBF7]">
          <p className="text-xs uppercase tracking-widest opacity-80 mb-2">{trip.country}</p>
          {editingName ? (
            <div className="flex items-center gap-2">
              <input
                data-testid="trip-name-input"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveName()}
                autoFocus
                className="bg-white/95 text-[#0A0A0A] rounded-lg px-3 py-1.5 font-display text-2xl sm:text-3xl outline-none max-w-full"
              />
              <button
                data-testid="trip-name-save"
                onClick={saveName}
                className="p-2 rounded-full bg-[#C8553D] hover:bg-[#a8412f] transition-colors"
              >
                <Check className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <h2 data-testid="trip-destination" className="font-display text-4xl sm:text-5xl tracking-tight">
                {displayName}
              </h2>
              {editable && (
                <button
                  data-testid="trip-name-edit"
                  onClick={() => {
                    setNameDraft(displayName);
                    setEditingName(true);
                  }}
                  title="Rename trip"
                  className="no-print p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
          {trip.custom_name && trip.custom_name !== trip.destination && (
            <p className="mt-1 text-sm opacity-80">{trip.destination}</p>
          )}
          {trip.tagline && <p className="mt-2 text-lg opacity-90 max-w-xl">{trip.tagline}</p>}
        </div>
      </div>

      <p className="text-lg leading-relaxed text-foreground/90 max-w-3xl">{trip.summary}</p>

      <div className="flex flex-wrap gap-3">
        {trip.estimated_budget && (
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F3EFE8] text-sm">
            <Wallet className="w-4 h-4 text-[#C8553D]" /> {trip.estimated_budget}
          </span>
        )}
        {trip.best_time_to_visit && (
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F3EFE8] text-sm">
            <Clock className="w-4 h-4 text-[#C8553D]" /> Best time: {trip.best_time_to_visit}
          </span>
        )}
        {trip.flights && trip.flights.estimate && (
          <span data-testid="flights-estimate" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1A3B2B] text-[#FDFBF7] text-sm">
            <Plane className="w-4 h-4 text-[#C8553D]" /> Flights{trip.flights.from ? ` from ${trip.flights.from}` : ""}: {trip.flights.estimate}
          </span>
        )}
      </div>
      {trip.flights && trip.flights.note && (
        <p className="text-xs text-muted-foreground -mt-2" data-testid="flights-note">
          Flight estimate (round-trip, per person, not included in the budget total) — {trip.flights.note}
        </p>
      )}

      {/* Trip budget */}
      {cb && Number(cb.total) > 0 && (
        <section data-testid="trip-budget">
          <h3 className="font-display text-3xl mb-6 flex items-center gap-3">
            <PiggyBank className="w-6 h-6 text-[#C8553D]" /> Trip budget
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <div className="lg:col-span-4 p-8 rounded-2xl bg-[#1A3B2B] text-[#FDFBF7] flex flex-col justify-center">
              <p className="text-xs uppercase tracking-widest opacity-80 mb-2">Estimated total</p>
              <p data-testid="budget-total" className="font-display text-5xl leading-none">{fmtMoney(cb.total)}</p>
              {cb.note && <p className="mt-4 text-sm opacity-85 leading-relaxed">{cb.note}</p>}
              {targetNum != null && (
                <div
                  data-testid="budget-status"
                  className={`mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                    overBudget ? "bg-[#C8553D] text-[#FDFBF7]" : "bg-[#FDFBF7]/15 text-[#FDFBF7]"
                  }`}
                >
                  {overBudget ? <AlertTriangle className="w-4 h-4" /> : <Star className="w-4 h-4" />}
                  {overBudget
                    ? `Over your target of ${fmtMoney(targetNum)} by ${fmtMoney(Number(cb.total) - targetNum)}`
                    : `Within your target of ${fmtMoney(targetNum)}`}
                </div>
              )}
            </div>
            <div className="lg:col-span-8 p-6 rounded-2xl border border-black/5 bg-white/60 space-y-4">
              {costRows.map((r) => (
                <div key={r.key}>
                  <div className="flex items-center justify-between mb-1.5 text-sm">
                    <span className="flex items-center gap-2">
                      <r.icon className="w-4 h-4 text-[#1A3B2B]" /> {r.label}
                    </span>
                    <span className="font-semibold text-[#1A3B2B]">{fmtMoney(cb[r.key])}</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-[#F3EFE8] overflow-hidden">
                    <div
                      className="h-full bg-[#C8553D]"
                      style={{ width: `${Math.round(((Number(cb[r.key]) || 0) / costMax) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Budget planner */}
      <BudgetPlanner trip={trip} editable={editable} onTripChange={onTripChange} />

      {/* Weather */}
      {trip.weather && (
        <section data-testid="trip-weather">
          <h3 className="font-display text-3xl mb-6 flex items-center gap-3">
            <CloudSun className="w-6 h-6 text-[#C8553D]" /> Weather peek
          </h3>
          {trip.weather.is_rough && trip.weather.better_time && (
            <div
              data-testid="weather-nudge"
              className="mb-5 p-5 rounded-2xl border border-[#C8553D]/40 bg-[#C8553D]/10 flex items-start gap-3"
            >
              <AlertTriangle className="w-5 h-5 text-[#C8553D] shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-[#1A3B2B]">
                  Heads up — {trip.weather.period} can be rough here.
                </p>
                <p className="text-sm text-foreground/80 mt-1">{trip.weather.better_time}</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <div className="lg:col-span-5 p-8 rounded-2xl bg-[#1A3B2B] text-[#FDFBF7]">
              <p className="text-xs uppercase tracking-widest opacity-80 mb-3">
                {trip.weather.period || "Typical weather"}
              </p>
              <div className="flex items-end gap-2">
                <Thermometer className="w-8 h-8 mb-1 text-[#C8553D]" />
                <span className="font-display text-5xl leading-none">
                  {trip.weather.temp_high_c}°
                </span>
                <span className="text-lg opacity-80 mb-1">/ {trip.weather.temp_low_c}°C</span>
              </div>
              <p className="mt-3 text-lg">{trip.weather.conditions}</p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="p-6 rounded-2xl border border-black/5 bg-white/60">
                <p className="text-muted-foreground leading-relaxed">{trip.weather.summary}</p>
              </div>
              <div className="p-6 rounded-2xl border border-black/5 bg-white/60 flex items-start gap-3">
                <Umbrella className="w-5 h-5 text-[#C8553D] shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-[#C8553D]">Rain</p>
                  <p className="font-medium">{trip.weather.rain}</p>
                </div>
              </div>
              {trip.weather.packing_tip && (
                <div className="sm:col-span-2 p-6 rounded-2xl border border-black/5 bg-[#F3EFE8] flex items-start gap-3">
                  <Luggage className="w-5 h-5 text-[#1A3B2B] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-[#1A3B2B]">Packing tip</p>
                    <p>{trip.weather.packing_tip}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Closed-day guard */}
      <ClosureGuard trip={trip} editable={editable} onTripChange={onTripChange} />

      {/* What's on — real events during the trip */}
      {Array.isArray(trip.events) && trip.events.length > 0 && (
        <section data-testid="whats-on-banner" className="rounded-3xl bg-[#1A3B2B] text-[#FDFBF7] p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-1">
            <PartyPopper className="w-6 h-6 text-[#C8553D]" />
            <h3 className="font-display text-3xl">What's on during your trip</h3>
          </div>
          <p className="text-sm opacity-80 mb-6">Real festivals, markets and happenings we found for your dates.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {trip.events.map((ev, i) => (
              <div key={i} data-testid={`whats-on-event-${i}`} className="rounded-2xl bg-[#FDFBF7]/8 border border-[#FDFBF7]/15 p-5">
                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                  <h4 className="font-display text-xl">{ev.name}</h4>
                  {ev.type && (
                    <span className="text-[11px] uppercase tracking-wide px-2.5 py-0.5 rounded-full bg-[#C8553D] text-[#FDFBF7]">{ev.type}</span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm opacity-90 mb-2 flex-wrap">
                  {ev.date && (
                    <span className="inline-flex items-center gap-1.5"><CalendarClock className="w-4 h-4 text-[#C8553D]" /> {ev.date}</span>
                  )}
                  {ev.day != null && ev.day !== "" && (
                    <span className="inline-flex items-center gap-1.5 text-[#FDFBF7]/80"><CalendarDays className="w-4 h-4" /> Day {ev.day}</span>
                  )}
                </div>
                {ev.description && <p className="text-sm leading-relaxed opacity-85">{ev.description}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Map */}
      <ItineraryMap trip={trip} editable={editable} onTripChange={onTripChange} />

      {/* Places */}
      <section>
        <h3 className="font-display text-3xl mb-6 flex items-center gap-3">
          <MapPin className="w-6 h-6 text-[#1A3B2B]" /> Places to visit
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {places.map((p, i) => (
            <motion.div
              key={i}
              custom={i}
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="p-6 rounded-2xl border border-black/5 bg-white/60"
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-display text-xl">{p.name}</h4>
                <span className="text-xs uppercase tracking-wide text-[#C8553D]">{p.category}</span>
              </div>
              {p.kid_friendly && (
                <span className="inline-flex items-center gap-1 mb-2 px-2 py-0.5 rounded-full bg-[#1A3B2B]/10 text-[#1A3B2B] text-xs font-medium">
                  <Baby className="w-3 h-3" /> Kid-friendly
                </span>
              )}
              <p className="text-muted-foreground leading-relaxed">{p.description}</p>
              {p.why && <p className="mt-2 text-sm text-[#1A3B2B]">Why: {p.why}</p>}
              <PlaceBadges item={p} />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Restaurants */}
      <section>
        <h3 className="font-display text-3xl mb-6 flex items-center gap-3">
          <Utensils className="w-6 h-6 text-[#1A3B2B]" /> Where to eat
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {restaurants.map((r, i) => (
            <motion.div
              key={i}
              custom={i}
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="p-6 rounded-2xl border border-black/5 bg-white/60 flex flex-col"
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <h4 className="font-display text-lg leading-tight">{r.name}</h4>
                <div className="flex items-center gap-1.5 shrink-0">
                  {r.vegetarian && (
                    <span
                      data-testid={`restaurant-veg-${i}`}
                      title="Vegetarian options available"
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-green-600 text-green-700 text-xs font-bold"
                    >
                      <Leaf className="w-3 h-3" /> V
                    </span>
                  )}
                  <span className="text-sm font-semibold text-[#1A3B2B]">{r.price_range}</span>
                </div>
              </div>
              <p className="text-xs uppercase tracking-wide text-[#C8553D] mb-2">{r.cuisine}</p>
              {r.kid_friendly && (
                <span className="inline-flex items-center gap-1 mb-2 px-2 py-0.5 rounded-full bg-[#1A3B2B]/10 text-[#1A3B2B] text-xs font-medium">
                  <Baby className="w-3 h-3" /> Kid-friendly
                </span>
              )}
              <p className="text-muted-foreground text-sm leading-relaxed">{r.description}</p>
              {r.must_try && <p className="mt-2 text-sm">Must try: <span className="font-medium">{r.must_try}</span></p>}
              <PlaceBadges item={r} />
              <a
                href={bookingLink(r, r.city)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`restaurant-link-${i}`}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#1A3B2B] hover:text-[#C8553D] transition-colors"
              >
                <ExternalLink className="w-4 h-4" /> Menu / Book
              </a>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Accommodation */}
      {accommodations.length > 0 && (
        <section data-testid="trip-accommodation">
          <h3 className="font-display text-3xl mb-2 flex items-center gap-3">
            <BedDouble className="w-6 h-6 text-[#1A3B2B]" /> Where to stay
          </h3>
          <p className="text-muted-foreground mb-6">Picked to match your budget and sit close to your itinerary.</p>
          {Object.entries(accomByCity).map(([city, list]) => (
            <div key={city} className="mb-8 last:mb-0">
              {Object.keys(accomByCity).length > 1 && (
                <p className="text-xs uppercase tracking-widest text-[#C8553D] mb-3">{city}</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {list.map((a, i) => (
                  <motion.div
                    key={i}
                    custom={i}
                    variants={stagger}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    data-testid="accommodation-card"
                    className="p-6 rounded-2xl border border-black/5 bg-white/60 flex flex-col"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className="font-display text-lg leading-tight">{a.name}</h4>
                      {a.rating && (
                        <span className="inline-flex items-center gap-1 text-sm text-[#1A3B2B] shrink-0">
                          <Star className="w-3.5 h-3.5 fill-[#C8553D] text-[#C8553D]" /> {a.rating}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs uppercase tracking-wide text-[#C8553D]">{a.type}</span>
                      {a.area && <span className="text-xs text-muted-foreground">· {a.area}</span>}
                    </div>
                    {a.price_per_night && (
                      <p className="text-sm font-semibold text-[#1A3B2B] mb-2">{a.price_per_night} <span className="font-normal text-muted-foreground">/ night</span></p>
                    )}
                    {a.why && <p className="text-muted-foreground text-sm leading-relaxed">{a.why}</p>}
                    {a.transport && (
                      <p className="mt-2 text-sm flex gap-2 items-start">
                        <Bus className="w-4 h-4 text-[#1A3B2B] shrink-0 mt-0.5" /> {a.transport}
                      </p>
                    )}
                    {a.accessibility_note && (
                      <p className="mt-2 text-sm flex gap-2 items-start text-[#1A3B2B]">
                        <Accessibility className="w-4 h-4 shrink-0 mt-0.5" /> {a.accessibility_note}
                      </p>
                    )}
                    <PlaceBadges item={a} />
                    <a
                      href={bookingLink(a, a.city)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="accommodation-link"
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#1A3B2B] hover:text-[#C8553D] transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" /> View / Book
                    </a>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Inter-city travel */}
      {transfers.length > 0 && (
        <section data-testid="trip-transfers">
          <h3 className="font-display text-3xl mb-6 flex items-center gap-3">
            <Route className="w-6 h-6 text-[#1A3B2B]" /> Getting between cities
          </h3>
          <div className="space-y-4">
            {transfers.map((t, i) => {
              const Icon = transferIcon(t.mode);
              return (
                <motion.div
                  key={i}
                  custom={i}
                  variants={stagger}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  data-testid="transfer-card"
                  className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-5 rounded-2xl border border-black/5 bg-white/60"
                >
                  <div className="flex items-center gap-2 sm:gap-3 shrink-0 font-display text-base sm:text-lg">
                    <span>{t.from}</span>
                    <ArrowRight className="w-4 h-4 text-[#C8553D] shrink-0" />
                    <span>{t.to}</span>
                  </div>
                  <div className="hidden sm:block w-px self-stretch bg-border" />
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
                    <span className="inline-flex items-center gap-1.5 font-medium text-[#1A3B2B]">
                      <Icon className="w-4 h-4" /> {t.mode}
                    </span>
                    {t.duration && (
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <Clock className="w-4 h-4" /> {t.duration}
                      </span>
                    )}
                    {t.cost && (
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <Wallet className="w-4 h-4" /> {t.cost}
                      </span>
                    )}
                    {t.note && <span className="text-[#C8553D]">{t.note}</span>}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>
      )}


      {/* Hidden gems */}
      <section>
        <div className="flex items-center gap-3 mb-6">
          <Gem className="w-6 h-6 text-[#C8553D]" />
          <h3 className="font-display text-3xl">Hidden gems</h3>
          <span className="text-sm text-muted-foreground">— from the locals</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {gems.map((g, i) => (
            <motion.div
              key={i}
              custom={i}
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden border border-black/5 bg-[#1A3B2B] text-[#FDFBF7]"
            >
              <img src={placePhotoUrl(g.google?.photo_name, 640) || GEM_IMG} onError={(e) => { e.currentTarget.src = GEM_IMG; }} alt={g.name} className="w-full h-32 object-cover opacity-90" />
              <div className="p-6">
                <h4 className="font-display text-xl mb-2">{g.name}</h4>
                <p className="text-sm opacity-90 leading-relaxed">{g.description}</p>
                {g.local_tip && (
                  <p className="mt-3 text-sm flex gap-2 items-start opacity-95">
                    <Quote className="w-4 h-4 shrink-0 mt-0.5 text-[#C8553D]" /> {g.local_tip}
                  </p>
                )}
                {g.recommended_by && (
                  <p className="mt-3 text-xs uppercase tracking-wide text-[#C8553D]">— {g.recommended_by}</p>
                )}
                <PlaceBadges item={g} dark />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Itinerary */}
      <section>
        <h3 className="font-display text-3xl mb-6 flex items-center gap-3">
          <CalendarDays className="w-6 h-6 text-[#1A3B2B]" /> Day-by-day itinerary
        </h3>

        {/* Why WanderlyMe chose this */}
        <div className="mb-6" data-testid="why-chosen-section">
          {why?.overall ? (
            <div className="rounded-2xl border border-[#1A3B2B]/15 bg-[#1A3B2B]/5 p-6">
              <p className="text-xs uppercase tracking-widest text-[#1A3B2B] mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-[#C8553D]" /> Why WanderlyMe chose this
              </p>
              <p data-testid="why-overall" className="text-foreground/90 leading-relaxed">{why.overall}</p>
            </div>
          ) : (
            <button
              data-testid="why-chosen-btn"
              onClick={fetchWhy}
              disabled={whyLoading}
              className="no-print inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#1A3B2B]/30 text-[#1A3B2B] text-sm hover:bg-[#1A3B2B]/5 transition-colors disabled:opacity-50"
            >
              {whyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4 text-[#C8553D]" />}
              {whyLoading ? "Thinking…" : "Why did WanderlyMe choose this plan?"}
            </button>
          )}
        </div>

        <div className="space-y-4">
          {itinerary.map((d, i) => (
            <motion.div
              key={i}
              custom={i}
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-12 gap-4 p-6 rounded-2xl border border-black/5 bg-white/60"
            >
              <div className="sm:col-span-3">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#1A3B2B] text-[#FDFBF7] font-display text-xl mb-2">
                  {d.day}
                </div>
                <h4 className="font-display text-xl leading-tight">{d.title}</h4>
                {d.city && <p className="text-xs uppercase tracking-wide text-[#1A3B2B] mt-1">{d.city}</p>}
                {d.budget_note && <p className="text-xs text-[#C8553D] mt-1">{d.budget_note}</p>}
                {editable && (
                  <button
                    data-testid={`regenerate-day-${d.day}`}
                    onClick={() => regenerateDay(d)}
                    disabled={busyDay === d.day}
                    className="no-print mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#C8553D] text-[#C8553D] text-xs hover:bg-[#C8553D] hover:text-[#FDFBF7] transition-colors disabled:opacity-50"
                  >
                    {busyDay === d.day ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5" />
                    )}
                    Reshuffle
                  </button>
                )}
              </div>
              <div className="sm:col-span-9 space-y-3">
                {["morning", "afternoon", "evening"].map((slot) =>
                  d[slot] ? (
                    <div key={slot} className="flex gap-3">
                      <span className="w-20 shrink-0 text-xs uppercase tracking-wide text-[#C8553D] pt-1">
                        {slot}
                      </span>
                      <p className="text-foreground/90 leading-relaxed">{d[slot]}</p>
                    </div>
                  ) : null
                )}
                {why?.days?.[String(d.day)] && (
                  <div data-testid={`why-day-${d.day}`} className="flex gap-2 items-start pt-1 text-sm text-[#1A3B2B] bg-[#1A3B2B]/5 rounded-lg px-3 py-2">
                    <Lightbulb className="w-4 h-4 shrink-0 mt-0.5 text-[#C8553D]" />
                    <span><span className="font-medium">Why this day: </span>{why.days[String(d.day)]}</span>
                  </div>
                )}
                {editable ? (
                  <div className="pt-1">
                    <label className="text-xs uppercase tracking-wide text-[#1A3B2B] flex items-center gap-1.5 mb-1">
                      <StickyNote className="w-3.5 h-3.5" /> Your notes
                    </label>
                    <textarea
                      data-testid={`day-note-${d.day}`}
                      value={d.note || ""}
                      onChange={(e) => updateDayNote(d.day, e.target.value)}
                      placeholder="Add a personal note for this day…"
                      rows={2}
                      className="w-full text-sm rounded-lg border border-border bg-white/70 px-3 py-2 outline-none focus:border-[#1A3B2B] transition-colors resize-y"
                    />
                  </div>
                ) : (
                  d.note && (
                    <div className="flex gap-2 items-start pt-1 text-sm text-[#1A3B2B] bg-[#F3EFE8] rounded-lg px-3 py-2">
                      <StickyNote className="w-4 h-4 shrink-0 mt-0.5" /> {d.note}
                    </div>
                  )
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Packing list */}
      {packing.length > 0 && (
        <section data-testid="trip-packing">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
            <h3 className="font-display text-3xl flex items-center gap-3">
              <ListChecks className="w-6 h-6 text-[#1A3B2B]" /> Packing list
            </h3>
            <button
              type="button"
              data-testid="packing-print-btn"
              onClick={printPacking}
              className="no-print inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#1A3B2B]/30 text-[#1A3B2B] text-sm hover:bg-[#1A3B2B]/5 transition-colors"
            >
              <Printer className="w-4 h-4" /> Printable list
            </button>
          </div>
          <p className="text-muted-foreground mb-4">Weather-aware essentials for this trip — tick them off as you pack.</p>
          {(() => {
            const packedCount = packing.filter((it) => checkedSet.has(it)).length;
            const pct = Math.round((packedCount / packing.length) * 100);
            return (
              <div className="mb-6 flex items-center gap-4" data-testid="packing-progress">
                <div className="flex-1 h-2.5 rounded-full bg-[#F3EFE8] overflow-hidden">
                  <div className="h-full bg-[#1A3B2B] transition-all duration-300" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-sm font-medium text-[#1A3B2B] shrink-0" data-testid="packing-count">
                  {packedCount} / {packing.length} packed
                </span>
                {onTripChange && packedCount > 0 && (
                  <button
                    type="button"
                    data-testid="packing-reset"
                    onClick={() => onTripChange({ ...trip, packing_checked: [] })}
                    className="no-print inline-flex items-center gap-1.5 text-sm text-[#C8553D] hover:underline shrink-0"
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Reset
                  </button>
                )}
              </div>
            );
          })()}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {packing.map((item, i) => {
              const on = checkedSet.has(item);
              return (
                <button
                  key={i}
                  type="button"
                  data-testid={`packing-item-${i}`}
                  onClick={() => togglePack(item)}
                  disabled={!onTripChange}
                  className={`flex items-center gap-3 text-left px-4 py-3 rounded-xl border transition-colors ${
                    onTripChange ? "cursor-pointer" : "cursor-default"
                  } ${
                    on ? "border-[#1A3B2B] bg-[#1A3B2B]/5 text-muted-foreground line-through" : "border-black/5 bg-white/60"
                  }`}
                >
                  {on ? (
                    <CheckSquare className="w-5 h-5 text-[#1A3B2B] shrink-0" />
                  ) : (
                    <Square className="w-5 h-5 text-[#C8553D] shrink-0" />
                  )}
                  <span className="text-sm">{item}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {editable && onTripChange && <TripAssistant trip={trip} onTripChange={onTripChange} />}
    </div>
  );
};



